import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header';
import CrisisResourceBanner from '../../components/CrisisResourceBanner';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CurrentSubscription from './components/CurrentSubscription';
import PlanComparison from './components/PlanComparison';
import BillingHistory from './components/BillingHistory';
import UsageAnalytics from './components/UsageAnalytics';
import PaymentMethods from './components/PaymentMethods';
import { subscriptionService } from '../../services/subscriptionService';
import { adminService } from '../../services/adminService';

const SubscriptionManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [organization, setOrganization] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [usageMetrics, setUsageMetrics] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'plans', label: 'Plans & Pricing', icon: 'Package' },
    { id: 'billing', label: 'Billing History', icon: 'Receipt' },
    { id: 'usage', label: 'Usage Analytics', icon: 'BarChart3' },
    { id: 'payment', label: 'Payment Methods', icon: 'CreditCard' }
  ];

  useEffect(() => {
    loadSubscriptionData();
  }, []);

  const loadSubscriptionData = async () => {
    setLoading(true);
    setError('');

    try {
      // Get organization
      const { data: orgData, error: orgError } = await adminService?.getOrganization();
      if (orgError) throw orgError;
      
      if (!orgData) {
        setError('Organization not found. Please set up your organization first.');
        setLoading(false);
        return;
      }

      setOrganization(orgData);

      // Load all subscription data in parallel
      const [
        plansResult,
        subscriptionResult,
        invoicesResult,
        metricsResult,
        paymentMethodsResult
      ] = await Promise.all([
        subscriptionService?.getSubscriptionPlans(),
        subscriptionService?.getOrganizationSubscription(orgData?.id),
        subscriptionService?.getOrganizationInvoices(orgData?.id),
        subscriptionService?.getUsageMetrics(orgData?.id, 30),
        subscriptionService?.getPaymentMethods(orgData?.id)
      ]);

      if (plansResult?.error) console.error('Plans error:', plansResult?.error);
      if (subscriptionResult?.error) console.error('Subscription error:', subscriptionResult?.error);
      if (invoicesResult?.error) console.error('Invoices error:', invoicesResult?.error);
      if (metricsResult?.error) console.error('Metrics error:', metricsResult?.error);
      if (paymentMethodsResult?.error) console.error('Payment methods error:', paymentMethodsResult?.error);

      setPlans(plansResult?.data || []);
      setSubscription(subscriptionResult?.data);
      setInvoices(invoicesResult?.data || []);
      setUsageMetrics(metricsResult?.data || []);
      setPaymentMethods(paymentMethodsResult?.data || []);
    } catch (err) {
      console.error('Load subscription data error:', err);
      setError(err?.message || 'Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId, billingCycle) => {
    if (!organization?.id) {
      setError('Organization not found');
      return;
    }

    try {
      const { data, error } = await subscriptionService?.createCheckoutSession(
        planId,
        billingCycle,
        organization?.id
      );

      if (error) throw error;

      if (data?.url) {
        window.location.href = data?.url;
      }
    } catch (err) {
      console.error('Upgrade error:', err);
      setError(err?.message || 'Failed to start upgrade process');
    }
  };

  const handleManageBilling = async () => {
    if (!organization?.id) {
      setError('Organization not found');
      return;
    }

    // Check if subscription exists before trying to open billing portal
    if (!subscription) {
      setError('You need to subscribe to a plan before accessing the billing portal. Please select a plan from the "Plans & Pricing" tab.');
      setActiveTab('plans');
      return;
    }

    try {
      const { data, error } = await subscriptionService?.createBillingPortalSession(
        organization?.id
      );

      if (error) {
        // Handle specific error messages from the Edge Function
        if (error?.message?.includes('No subscription found') || error?.message?.includes('No Stripe customer found')) {
          setError('You need to subscribe to a plan before accessing the billing portal. Please select a plan from the "Plans & Pricing" tab.');
          setActiveTab('plans');
          return;
        }
        throw error;
      }

      if (data?.url) {
        window.location.href = data?.url;
      }
    } catch (err) {
      console.error('Billing portal error:', err);
      setError(err?.message || 'Failed to open billing portal');
    }
  };

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center space-y-4">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto" />
            <p className="text-muted-foreground">Loading subscription data...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
          <p className="text-destructive font-medium mb-2">Error Loading Data</p>
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          <Button onClick={loadSubscriptionData} iconName="RefreshCw" iconPosition="left">
            Retry
          </Button>
        </div>
      );
    }

    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <CurrentSubscription 
              subscription={subscription}
              onManageBilling={handleManageBilling}
              onRefresh={loadSubscriptionData}
            />
            <UsageAnalytics 
              metrics={usageMetrics}
              subscription={subscription}
            />
          </div>
        );
      case 'plans':
        return (
          <PlanComparison 
            plans={plans}
            currentSubscription={subscription}
            onUpgrade={handleUpgrade}
          />
        );
      case 'billing':
        return (
          <BillingHistory 
            invoices={invoices}
            onRefresh={loadSubscriptionData}
          />
        );
      case 'usage':
        return (
          <UsageAnalytics 
            metrics={usageMetrics}
            subscription={subscription}
            detailed={true}
          />
        );
      case 'payment':
        return (
          <PaymentMethods 
            paymentMethods={paymentMethods}
            onManageBilling={handleManageBilling}
            onRefresh={loadSubscriptionData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Subscription Management - Cwtch Platform</title>
        <meta name="description" content="Manage your organization's subscription plans, billing, and payment processing" />
      </Helmet>
      <CrisisResourceBanner />
      <Header />
      <div className="min-h-screen bg-background pt-32">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="CreditCard" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold">Subscription Management</h1>
                  <p className="text-muted-foreground">Manage billing, plans, and payment processing</p>
                </div>
              </div>
              {subscription && (
                <Button 
                  onClick={handleManageBilling}
                  iconName="ExternalLink"
                  iconPosition="right"
                  variant="outline"
                >
                  Manage Billing
                </Button>
              )}
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-card border border-border rounded-lg mb-6">
            <div className="flex overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-background">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </>
  );
};

export default SubscriptionManagement;